import React, { memo, useEffect, useState } from "react";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  FormGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../../redux/reducers/LoaderTrigger";
import configServ from "../../../services/config";
import AlertDialog from "../../AlertDialog/AlertDialog";

function AddLocationDialog({ open, handleClose, onLocationAdded }) {
  const dispatch = useDispatch();
  const { user_id, companyCountry, companyState } = useSelector(
    (state) => state.GlobalVariables
  );
  const { company_type } = useSelector((state) => state.admin);

  const initialFormData = {
    location_name: "",
    address: "",
    latitude: "",
    longitude: "",
    country: companyCountry || "India",
    state: companyState || "",
    city: "",
    pin: "",
    company_type: company_type || "product",
    status: true,
    isPrimary: false,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [validationErrors, setValidationErrors] = useState({});
  const [countryList, setCountryList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [title, setTitle] = useState("Alert");
  const [dialogContent, setDialogContent] = useState("");
  const [buttonType, setButtonType] = useState(null);

  const handleOpenDialog = (heading, content, type) => {
    setTitle(heading);
    setDialogContent(content);
    setButtonType(type);
    setDialogOpen(true);
  };

  const handleCloseAlertDialog = () => {
    setDialogOpen(false);
    setTitle("Alert");
    setDialogContent("");
    setButtonType(null);
  };

  const clear = () => {
    setFormData({
      ...initialFormData,
      country: companyCountry || "India",
      state: companyState || "",
      company_type: company_type || "product",
    });
    setValidationErrors({});
  };

  const handleModalClose = () => {
    clear();
    handleClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target;
    setFormData((state) => ({
      ...state,
      [name]: checked,
    }));
  };

  const fetchCountries = async () => {
    try {
      const res = await configServ.getCountry();
      if (res.status === 200) {
        setCountryList(res.data);
      } else {
        setCountryList([]);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const fetchStates = async () => {
    try {
      const matchedCountry = countryList.find(
        (x) => x.country === formData.country
      );
      if (!matchedCountry) return;
      const countryId = matchedCountry.PK;
      const res = await configServ.getState({ country: countryId });
      if (res.status === 200) {
        setStateList(res.data);
      } else {
        setStateList([]);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (open) {
      clear();
      fetchCountries();
    }
  }, [open]);

  useEffect(() => {
    if (formData.country && countryList.length > 0) {
      fetchStates();
    }
  }, [formData.country, countryList]);

  const validate = () => {
    let errors = {};
    if (!formData.location_name || !formData.location_name.trim()) {
      errors.location_name = "required";
    }
    if (!formData.country) {
      errors.country = "required";
    }
    if (!formData.state) {
      errors.state = "required";
    }
    if (!formData.city || !formData.city.trim()) {
      errors.city = "required";
    }
    if (!formData.company_type) {
      errors.company_type = "required";
    }

    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    try {
      const isValid = validate();
      if (!isValid) {
        return false;
      }

      dispatch(triggerLoader());
      const dataToSend = {
        ...formData,
        admin_id: user_id,
        company_type: formData.company_type || company_type || "product",
      };

      const res = await configServ.addLocation(dataToSend);
      if (res.status === 200) {
        clear();
        if (onLocationAdded) {
          onLocationAdded(res.data);
        }
        handleClose();
      } else if (res.status === 400) {
        handleOpenDialog(
          "Warning",
          res.message || "Failed to create location.",
          "warning"
        );
      } else {
        handleOpenDialog(
          "Error",
          res.message || "Failed to create location.",
          "error"
        );
      }
    } catch (e) {
      console.log(e);
      handleOpenDialog("Error", "An unexpected error has occurred.", "error");
    } finally {
      dispatch(triggerLoader());
    }
  };

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseAlertDialog}
        title={title}
        message={dialogContent}
        buttonTitle="Ok"
        buttonType={buttonType}
      />

      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ backgroundColor: "#e3f3fc", py: 1.5 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight="bold" color="#1e61b4">
              Add New Location
            </Typography>
            <IconButton onClick={handleModalClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: "#f9fbfe", py: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                sx={{ fontSize: "16px", fontWeight: "bold", color: "#1e61b4" }}
              >
                Location Detail
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={
                  <span>
                    Location Name<span style={{ color: "red" }}>*</span>
                  </span>
                }
                variant="outlined"
                name="location_name"
                type="text"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                onChange={handleChange}
                value={formData.location_name || ""}
                error={!!validationErrors.location_name}
                helperText={validationErrors.location_name}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Address"
                variant="outlined"
                name="address"
                type="text"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                onChange={handleChange}
                value={formData.address || ""}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Latitude"
                variant="outlined"
                name="latitude"
                type="text"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                onChange={handleChange}
                value={formData.latitude || ""}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Longitude"
                variant="outlined"
                name="longitude"
                type="text"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                onChange={handleChange}
                value={formData.longitude || ""}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" error={!!validationErrors.country}>
                <InputLabel>
                  Country<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  name="country"
                  label="Country*"
                  value={formData.country || ""}
                  onChange={handleChange}
                >
                  <MenuItem value="">Select Country</MenuItem>
                  {countryList.map((item, index) => (
                    <MenuItem key={index} value={item.country}>
                      {item.country}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" error={!!validationErrors.state}>
                <InputLabel>
                  State<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  name="state"
                  label="State*"
                  value={formData.state || ""}
                  onChange={handleChange}
                >
                  <MenuItem value="">Select State</MenuItem>
                  {stateList.map((item, index) => (
                    <MenuItem key={index} value={item.State}>
                      {item.State}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={
                  <span>
                    City<span style={{ color: "red" }}>*</span>
                  </span>
                }
                variant="outlined"
                name="city"
                type="text"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                onChange={handleChange}
                value={formData.city || ""}
                error={!!validationErrors.city}
                helperText={validationErrors.city}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Pin"
                variant="outlined"
                name="pin"
                type="number"
                fullWidth
                size="small"
                sx={{ backgroundColor: "white" }}
                onChange={handleChange}
                value={formData.pin || ""}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small" error={!!validationErrors.company_type}>
                <InputLabel>
                  Company Type<span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  name="company_type"
                  label="Company Type*"
                  value={formData.company_type || "product"}
                  onChange={handleChange}
                  sx={{ backgroundColor: "white" }}
                >
                  <MenuItem value="product">product</MenuItem>
                  <MenuItem value="service">service</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={8}>
              <FormGroup sx={{ display: "flex", flexDirection: "row", mt: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox checked={Boolean(formData.status) || false} />
                  }
                  label="Status"
                  name="status"
                  onChange={handleChangeCheckbox}
                  style={{ marginRight: "20px" }}
                />
                <FormControlLabel
                  name="isPrimary"
                  control={
                    <Checkbox checked={Boolean(formData.isPrimary) || false} />
                  }
                  onChange={handleChangeCheckbox}
                  label="Is Primary"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#e3f3fc", px: 3, py: 1.5 }}>
          <Button
            onClick={clear}
            variant="contained"
            color="secondary"
            startIcon={<RotateLeftIcon />}
            sx={{ mx: 1 }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={<SaveIcon />}
            color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(AddLocationDialog);
